'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Maximize2,
  Minimize2,
  Pause,
  PictureInPicture2,
  Play,
  RotateCcw,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Slider } from '@kit/ui/slider';
import { cn } from '@kit/ui/utils';

export interface VideoPlayerProps {
  src: string;
  poster?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  className?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: (error: Error) => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

export interface VideoPlayerRef {
  play: () => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  pause: () => void;
  seek: (time: number) => void;
  toggleFullscreen: () => void;
  togglePictureInPicture: () => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  getCurrentTime: () => number;
  getDuration: () => number;
}

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

/**
 * Feature-rich video player component
 *
 * @example
 * ```tsx
 * <VideoPlayer
 *   src={videoUrl}
 *   poster={posterUrl}
 *   controls
 *   onEnded={() => console.log('Video ended')}
 * />
 * ```
 */
export const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  function VideoPlayer(
    {
      src,
      poster,
      autoPlay = false,
      muted: initialMuted = false,
      loop = false,
      controls = true,
      className,
      onPlay,
      onPause,
      onEnded,
      onError,
      onTimeUpdate,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(initialMuted);
    const [volume, setVolume] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPiP, setIsPiP] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [showControls, setShowControls] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const hideControlsTimeout = useRef<NodeJS.Timeout | null>(null);

    // Handle play/pause
    const togglePlay = useCallback(async () => {
      if (!videoRef.current) return;

      if (isPlaying) {
        videoRef.current.pause();
      } else {
        await videoRef.current.play();
      }
    }, [isPlaying]);

    // Handle mute
    const toggleMute = useCallback(() => {
      if (!videoRef.current) return;

      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }, [isMuted]);

    // Handle volume change
    const handleVolumeChange = useCallback((value: number[]) => {
      if (!videoRef.current || !value[0]) return;

      const newVolume = value[0];
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }, []);

    // Handle seek
    const handleSeek = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!videoRef.current || !progressRef.current) return;

        const rect = progressRef.current.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const time = percent * duration;
        videoRef.current.currentTime = time;
      },
      [duration],
    );

    // Handle fullscreen
    const handleFullscreen = useCallback(async () => {
      if (!containerRef.current) return;

      if (!isFullscreen) {
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
      }
    }, [isFullscreen]);

    // Handle picture-in-picture
    const handlePiP = useCallback(async () => {
      if (!videoRef.current) return;

      try {
        if (!isPiP) {
          await videoRef.current.requestPictureInPicture();
        } else {
          await document.exitPictureInPicture();
        }
      } catch (err) {
        console.error('PiP error:', err);
      }
    }, [isPiP]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      play: async () => {
        await videoRef.current?.play();
      },
      pause: () => {
        videoRef.current?.pause();
      },
      seek: (time: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
        }
      },
      toggleFullscreen: () => {
        handleFullscreen();
      },
      togglePictureInPicture: async () => {
        await handlePiP();
      },
      getCurrentTime: () => videoRef.current?.currentTime || 0,
      getDuration: () => videoRef.current?.duration || 0,
    }));

    // Handle playback rate change
    const cyclePlaybackRate = useCallback(() => {
      if (!videoRef.current) return;

      const currentIndex = PLAYBACK_RATES.indexOf(playbackRate);
      const nextIndex = (currentIndex + 1) % PLAYBACK_RATES.length;
      const newRate = PLAYBACK_RATES[nextIndex] ?? 1;
      videoRef.current.playbackRate = newRate;
      setPlaybackRate(newRate);
    }, [playbackRate]);

    // Reset controls hide timeout
    const resetControlsTimeout = useCallback(() => {
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
      setShowControls(true);

      if (isPlaying) {
        hideControlsTimeout.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
    }, [isPlaying]);

    // Handle mouse move to show controls
    const handleMouseMove = useCallback(() => {
      resetControlsTimeout();
    }, [resetControlsTimeout]);

    // Handle keyboard shortcuts
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!videoRef.current) return;

        switch (e.key) {
          case ' ':
          case 'k':
            e.preventDefault();
            togglePlay();
            break;
          case 'm':
            toggleMute();
            break;
          case 'f':
            handleFullscreen();
            break;
          case 'ArrowLeft':
            videoRef.current.currentTime -= 5;
            break;
          case 'ArrowRight':
            videoRef.current.currentTime += 5;
            break;
          case 'ArrowUp':
            e.preventDefault();
            handleVolumeChange([Math.min(1, volume + 0.1)]);
            break;
          case 'ArrowDown':
            e.preventDefault();
            handleVolumeChange([Math.max(0, volume - 0.1)]);
            break;
        }
      };

      const container = containerRef.current;
      if (container) {
        container.addEventListener('keydown', handleKeyDown);
        return () => container.removeEventListener('keydown', handleKeyDown);
      }
    }, [togglePlay, toggleMute, handleFullscreen, handleVolumeChange, volume]);

    // Video event handlers
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const handlePlay = () => {
        setIsPlaying(true);
        onPlay?.();
      };

      const handlePauseEvent = () => {
        setIsPlaying(false);
        onPause?.();
      };

      const handleTimeUpdateEvent = () => {
        setCurrentTime(video.currentTime);
        onTimeUpdate?.(video.currentTime, video.duration);
      };

      const handleLoadedMetadata = () => {
        setDuration(video.duration);
        setIsLoading(false);
      };

      const handleEndedEvent = () => {
        setIsPlaying(false);
        onEnded?.();
      };

      const handleErrorEvent = () => {
        setError(t('documents:video.failedToLoad'));
        setIsLoading(false);
        onError?.(new Error('Failed to load video'));
      };

      const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
      };

      const handlePiPChange = () => {
        setIsPiP(document.pictureInPictureElement === video);
      };

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePauseEvent);
      video.addEventListener('timeupdate', handleTimeUpdateEvent);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('ended', handleEndedEvent);
      video.addEventListener('error', handleErrorEvent);
      video.addEventListener('enterpictureinpicture', handlePiPChange);
      video.addEventListener('leavepictureinpicture', handlePiPChange);
      document.addEventListener('fullscreenchange', handleFullscreenChange);

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePauseEvent);
        video.removeEventListener('timeupdate', handleTimeUpdateEvent);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('ended', handleEndedEvent);
        video.removeEventListener('error', handleErrorEvent);
        video.removeEventListener('enterpictureinpicture', handlePiPChange);
        video.removeEventListener('leavepictureinpicture', handlePiPChange);
        document.removeEventListener(
          'fullscreenchange',
          handleFullscreenChange,
        );
      };
    }, [onPlay, onPause, onEnded, onError, onTimeUpdate, t]);

    // Format time
    const formatTime = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
      <div
        ref={containerRef}
        className={cn(
          'group relative overflow-hidden rounded-lg bg-black',
          className,
        )}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setShowControls(false)}
        tabIndex={0}
        role="application"
        aria-label={t('documents:video.playerLabel')}
      >
        {/* Video element */}
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          autoPlay={autoPlay}
          muted={initialMuted}
          loop={loop}
          playsInline
          className="h-full w-full"
          onClick={togglePlay}
        />

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="border-primary h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="text-center text-white">
              <p>{error}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                  videoRef.current?.load();
                }}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                {t('documents:video.retry')}
              </Button>
            </div>
          </div>
        )}

        {/* Controls overlay */}
        {controls && !error && (
          <div
            className={cn(
              'absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300',
              showControls ? 'opacity-100' : 'opacity-0',
            )}
          >
            {/* Progress bar */}
            <div
              ref={progressRef}
              className="mb-3 h-1 cursor-pointer rounded-full bg-white/30"
              onClick={handleSeek}
              tabIndex={0}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progressPercent)}
              aria-label={t('documents:video.progressLabel')}
            >
              <div
                className="bg-primary h-full rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            {/* Controls row */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {/* Play/Pause */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white hover:bg-white/20"
                  onClick={togglePlay}
                  aria-label={
                    isPlaying
                      ? t('documents:video.pause')
                      : t('documents:video.play')
                  }
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5" />
                  )}
                </Button>

                {/* Volume */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white hover:bg-white/20"
                    onClick={toggleMute}
                    aria-label={
                      isMuted
                        ? t('documents:video.unmute')
                        : t('documents:video.mute')
                    }
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX className="h-5 w-5" />
                    ) : (
                      <Volume2 className="h-5 w-5" />
                    )}
                  </Button>
                  <Slider
                    value={[isMuted ? 0 : volume]}
                    onValueChange={handleVolumeChange}
                    max={1}
                    step={0.1}
                    className="w-20"
                  />
                </div>

                {/* Time */}
                <span className="text-xs text-white">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {/* Playback rate */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs text-white hover:bg-white/20"
                  onClick={cyclePlaybackRate}
                  aria-label={t('documents:video.playbackSpeed', {
                    rate: playbackRate,
                  })}
                >
                  {playbackRate}x
                </Button>

                {/* Picture in Picture */}
                {document.pictureInPictureEnabled && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white hover:bg-white/20"
                    onClick={handlePiP}
                    aria-label={t('documents:video.pictureInPicture')}
                  >
                    <PictureInPicture2 className="h-5 w-5" />
                  </Button>
                )}

                {/* Fullscreen */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white hover:bg-white/20"
                  onClick={handleFullscreen}
                  aria-label={
                    isFullscreen
                      ? t('documents:video.exitFullscreen')
                      : t('documents:video.fullscreen')
                  }
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-5 w-5" />
                  ) : (
                    <Maximize2 className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Center play button (when paused) */}
        {!isPlaying && !isLoading && !error && showControls && (
          // eslint-disable-next-line kit-components/use-kit-primitives -- 80px circular center-play overlay (p-4 around a 48px h-12 w-12 icon); kit Button's forced h-9 height clips the oversized icon and breaks the circle
          <button
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/50 p-4 text-white transition-transform hover:scale-110"
            onClick={togglePlay}
            aria-label={t('documents:video.play')}
          >
            <Play className="h-12 w-12" />
          </button>
        )}
      </div>
    );
  },
);
